import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Route, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ChildAvatar from '@/components/ChildAvatar';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addPdfHeader, getPdfHeaderHeight } from '@/utils/pdfHelper';

const GroupsNew = () => {
  const [activities, setActivities] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedActivities, setExpandedActivities] = useState({});
  
  // Modal de trilha
  const [trilhaModalOpen, setTrilhaModalOpen] = useState(false);
  const [trilhaData, setTrilhaData] = useState(null);
  const [trilhaLoading, setTrilhaLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [activitiesRes, enrollmentsRes, childrenRes] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/enrollments`),
        axios.get(`${API}/children?active_only=true`)
      ]);
      
      setActivities(activitiesRes.data);
      setEnrollments(enrollmentsRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getChildrenByActivityDaySlot = (activityId, day, slot) => {
    const activityEnrollments = enrollments.filter(e => 
      e.activity_id === activityId && 
      e.time_slots && 
      e.time_slots[day] === slot
    );
    
    return activityEnrollments.map(e => {
      const child = children.find(c => c.id === e.child_id);
      return child;
    }).filter(Boolean);
  };

  const toggleActivity = (activityId) => {
    setExpandedActivities(prev => ({
      ...prev,
      [activityId]: !prev[activityId]
    }));
  };

  const getSlotTime = (shift, slot) => {
    if (shift === 'Matutino') {
      return slot === 1 ? '8h45-9h30' : slot === 2 ? '9h30-10h15' : '10h15-11h00';
    } else {
      return slot === 1 ? '14h00-14h45' : slot === 2 ? '14h45-15h30' : '15h30-16h15';
    }
  };

  const getDayLabel = (day) => {
    const labels = {
      'segunda': 'Segunda-feira',
      'terca': 'Terça-feira',
      'quarta': 'Quarta-feira',
      'quinta': 'Quinta-feira',
      'sexta': 'Sexta-feira'
    };
    return labels[day] || day;
  };

  // Função para buscar trilha
  const fetchTrilha = async (activity, day, slot) => {
    setTrilhaLoading(true);
    setTrilhaModalOpen(true);
    try {
      const response = await axios.get(
        `${API}/activities/${activity.id}/turmas/${day}/${activity.shift}/${slot}/trilha`
      );
      setTrilhaData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar trilha');
      setTrilhaModalOpen(false);
    } finally {
      setTrilhaLoading(false);
    }
  };

  // Função para exportar trilha em PDF
  const exportTrilhaPDF = () => {
    if (!trilhaData || !trilhaData.children?.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let startY = addPdfHeader(doc);

    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(`Trilha - ${trilhaData.activity_name}`, pageWidth / 2, startY, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.text(`${trilhaData.day_label} - ${trilhaData.shift_label} - ${trilhaData.slot_label}`, pageWidth / 2, startY + 7, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Total: ${trilhaData.total_children} criancas`, pageWidth / 2, startY + 13, { align: 'center' });

    const removeAccents = (str) => {
      if (!str) return '';
      return String(str)
        .replace(/[ãâáàä]/gi, 'a')
        .replace(/[êéèë]/gi, 'e')
        .replace(/[íìïî]/gi, 'i')
        .replace(/[õôóòö]/gi, 'o')
        .replace(/[úùüû]/gi, 'u')
        .replace(/[ç]/gi, 'c')
        .replace(/[ñ]/gi, 'n');
    };

    const maxActivities = Math.max(...trilhaData.children.map(c => c.trilha?.length || 0), 3);
    
    const headers = ['Crianca'];
    for (let i = 1; i <= maxActivities; i++) {
      headers.push(`${i}o Horario`);
    }

    const rows = trilhaData.children.map(child => {
      const row = [removeAccents(child.name)];
      for (let i = 0; i < maxActivities; i++) {
        const act = child.trilha?.[i];
        row.push(act ? removeAccents(act.activity_name) : '-');
      }
      return row;
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: startY + 18,
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [239, 246, 255] },
      columnStyles: { 0: { cellWidth: 60 } }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Gerado em ${new Date().toLocaleDateString('pt-BR')} - Pagina ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const fileName = `trilha_${removeAccents(trilhaData.activity_name)}_${trilhaData.day}_${trilhaData.shift}.pdf`;
    doc.save(fileName.replace(/\s+/g, '_').toLowerCase());
    toast.success('PDF exportado com sucesso!');
  };

  if (loading) return <div>Carregando...</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800">Grupos por Atividade e Horário</h1>
        <p className="text-slate-600 mt-1">Visualize as crianças matriculadas por atividade, dia e horário</p>
      </div>

      <div className="space-y-4">
        {activities.map(activity => {
          const isExpanded = expandedActivities[activity.id];
          
          return (
            <div key={activity.id} className="border border-slate-200 rounded-lg bg-white">
              <div 
                className="p-4 cursor-pointer hover:bg-slate-50 flex items-center justify-between"
                onClick={() => toggleActivity(activity.id)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <div>
                    <h3 className="font-semibold text-slate-800">{activity.name}</h3>
                    <p className="text-sm text-slate-500">{activity.shift}</p>
                  </div>
                </div>
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 border-t border-slate-100">
                  {activity.days_of_week.map(day => (
                    <div key={day} className="mt-4">
                      <h4 className="font-medium text-slate-700 mb-2">{getDayLabel(day)}</h4>
                      
                      <div className="space-y-3 ml-4">
                        {[1, 2, 3].map(slot => {
                          const childrenInSlot = getChildrenByActivityDaySlot(activity.id, day, slot);
                          
                          if (childrenInSlot.length === 0) return null;
                          
                          return (
                            <div key={slot} className="border-l-2 border-blue-300 pl-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-medium text-slate-600">
                                  {getSlotTime(activity.shift, slot)}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => fetchTrilha(activity, day, slot)}
                                  className="text-xs h-7 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <Route className="h-3 w-3 mr-1" />
                                  Ver Trilha
                                </Button>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {childrenInSlot.map(child => (
                                  <span 
                                    key={child.id}
                                    className="inline-block bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                                  >
                                    {child.name}
                                  </span>
                                ))}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Total: {childrenInSlot.length} matrículas
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal de Trilha */}
      <Dialog open={trilhaModalOpen} onOpenChange={setTrilhaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Route className="w-5 h-5" />
                Trilha da Turma
              </div>
              {trilhaData && (
                <Button
                  onClick={exportTrilhaPDF}
                  size="sm"
                  variant="outline"
                  className="ml-4"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Exportar PDF
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {trilhaLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-slate-600">Carregando trilha...</div>
            </div>
          ) : trilhaData ? (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-800">{trilhaData.activity_name}</h3>
                <p className="text-sm text-slate-600">
                  {trilhaData.day_label} - {trilhaData.shift_label} - {trilhaData.slot_label}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Total: {trilhaData.total_children} criança(s)
                </p>
              </div>

              {trilhaData.children && trilhaData.children.length > 0 ? (
                <div className="space-y-3">
                  {trilhaData.children.map((child, index) => (
                    <div key={child.id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <ChildAvatar name={child.name} photo={child.photo_url} size="sm" zoomable={true} />
                        <div>
                          <h4 className="font-medium text-slate-800">{child.name}</h4>
                          <p className="text-sm text-slate-500">Trilha de atividades</p>
                        </div>
                      </div>
                      
                      {child.trilha && child.trilha.length > 0 ? (
                        <div className="space-y-2">
                          {child.trilha.map((activity, actIndex) => (
                            <div key={actIndex} className="flex items-center gap-3 p-2 bg-slate-50 rounded">
                              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                {actIndex + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-800">{activity.activity_name}</p>
                                <p className="text-sm text-slate-500">{activity.time_label}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 italic">Nenhuma atividade na trilha</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  Nenhuma criança encontrada para esta turma
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              Erro ao carregar dados da trilha
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsNew;
